import React from "react"
import Link from "next/link"
import clsx from "clsx"

import { ViewerBackgroundType, ViewerFontSize, ViewerFontType } from "types"
import CloseButton from "../CloseButton"
import Divider from "../Divider"
import Settings from "../Settings"
import TieupImg from "src/components/common/TieupImg"
import styles from "./index.module.scss"
import routes from "routes"
import { getFacebookShareLink } from "src/libs/snsShareLink/getFacebookShareLink"
import { getTwitterShareLink } from "src/libs/snsShareLink/getTwitterShareLink"
import { getLineShareLink } from "src/libs/snsShareLink/getLineShareLink"
import { Creator } from "src/models/v2/creator/creator"
import { SizeType } from "../../common/TieupImg/SizeType"

export type DrawerProps = {
  isOpen: boolean
  title: string
  creators: Creator[]
  shareUrl: string
  backgroundType: ViewerBackgroundType
  fontType: ViewerFontType
  fontSize: ViewerFontSize
  onChangeBackgroundType: (backgroundType: ViewerBackgroundType) => void
  onChangeFontType: (fontType: ViewerFontType) => void
  onChangeFontSize: (fontSize: ViewerFontSize) => void
  onClose: () => void
  onResize: () => void
  showShare?: boolean
  chapters?: {
    id: string
    label: string
    link: string
  }[]
  currentChapterId?: string
}

const Drawer: React.FC<DrawerProps> = ({
  isOpen,
  title,
  creators,
  shareUrl,
  backgroundType,
  fontType,
  fontSize,
  onChangeBackgroundType,
  onChangeFontType,
  onChangeFontSize,
  onClose,
  onResize,
  showShare = true,
  chapters,
  currentChapterId,
}) => {
  return (
    <>
      <div className={clsx([styles.drawer, isOpen && styles.isActive])}>
        <div className={styles.workInfo}>
          <div className={styles.title}>{title}</div>

          <ul className={styles.authorList}>
            {creators.map((creator, index) => (
              <li className={styles.authorListItem} key={index}>
                <div className={styles.authorListItem_icon}>
                  <TieupImg
                    src={creator.icon_url || "/img/user-icon.png"}
                    size={SizeType.s}
                  />
                </div>

                <div className={styles.authorListItem_profile}>
                  <p className={styles.authorListItem_profile_roll}>
                    {creator.label}
                  </p>
                  <p className={styles.authorListItem_profile_name}>
                    {creator.name}
                  </p>
                </div>
                <div className={styles.authorListItem_button}>
                  <Link href={routes.userGift(creator.display_name)}>
                    <a target="_blank" rel="noreferrer">
                      ギフト
                    </a>
                  </Link>
                </div>
              </li>
            ))}
          </ul>
          {showShare && (
            <div className={styles.snsShare}>
              <h3 className={styles.snsShare_title}>作品を共有する</h3>
              <ul className={styles.snsShare_list}>
                <li className={styles.button}>
                  <a
                    href={getTwitterShareLink(shareUrl)}
                    target="_blank"
                    rel="noreferrer"
                    className={styles.anchor}
                  >
                    <TieupImg
                      className={styles.anchor_icon}
                      src={"/img/icn_twitter_01.png"}
                    />
                  </a>
                </li>

                <li className={styles.button}>
                  <a
                    href={getFacebookShareLink(shareUrl)}
                    target="_blank"
                    rel="noreferrer"
                    className={styles.anchor}
                  >
                    <TieupImg
                      className={styles.anchor_icon}
                      src={"/img/icn_facebook_01.png"}
                    />
                  </a>
                </li>

                <li className={styles.button}>
                  <a
                    href={getLineShareLink(shareUrl)}
                    target="_blank"
                    rel="noreferrer"
                    className={styles.anchor}
                  >
                    <TieupImg
                      className={styles.anchor_icon}
                      src={"/img/icn_line_01.png"}
                    />
                  </a>
                </li>
              </ul>
            </div>
          )}
        </div>
        <Divider />
        <div className={styles.settings}>
          <Settings
            backgroundType={backgroundType}
            fontType={fontType}
            fontSize={fontSize}
            onChangeBackgroundType={onChangeBackgroundType}
            onChangeFontSize={onChangeFontSize}
            onChangeFontType={onChangeFontType}
            onResize={onResize}
          />
        </div>
        {chapters && (
          <>
            <Divider />
            <div className={styles.ChapterIndex}>
              <h4 className={styles.ChapterIndex_h}>目次</h4>
              <ul className={styles.Chapters}>
                {chapters.map((chapter) => (
                  <li
                    key={chapter.id}
                    className={`${styles.ChaptersItems} ${
                      chapter.id === currentChapterId?.toString()
                        ? styles["-current"]
                        : ""
                    }`}
                  >
                    {chapter.id === currentChapterId?.toString() ? (
                      <span>{chapter.label}</span>
                    ) : (
                      <Link href={chapter.link}>
                        <a>{chapter.label}</a>
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </>
        )}
        <div className={styles.closeButton}>
          <CloseButton onClick={onClose} />
        </div>
      </div>
      <div
        className={clsx([styles.overlay, isOpen && styles.isActive])}
        onClick={onClose}
      />
    </>
  )
}

export default Drawer
